import { PickupSelection } from '../types';

export interface StoredPickupSelection extends PickupSelection {}

const buildStorageKey = (storeId: string, userKey?: string) =>
  `store-pickup:${userKey || 'guest'}:${storeId}`;

const buildRequestStorageKey = (storeId: string, userKey?: string) =>
  `store-request:${userKey || 'guest'}:${storeId}`;

const PICKUP_REQUEST_PREFIX = '[픽업 예정]';
const MESSAGE_REQUEST_PREFIX = '[요청 메시지]';
const PICKUP_TIME_WITH_SUFFIX_REGEX = /(\d{1,2}:\d{2}(?:\s*\([^()]+\))?)\s*$/;

export const normalizePickupLabel = (pickupLabel?: string | null) => {
  const trimmedLabel = pickupLabel?.trim() || '';
  if (!trimmedLabel) {
    return '';
  }

  const matchedTimeLabel = trimmedLabel.match(PICKUP_TIME_WITH_SUFFIX_REGEX);
  return matchedTimeLabel?.[1]?.trim() || trimmedLabel;
};

export const buildStoredPickupLabel = (
  _dateValue: string,
  hour: number,
  extraLabel?: string | null,
) => {
  const suffix = extraLabel?.trim() ? ` (${extraLabel.trim()})` : '';
  return `${hour.toString().padStart(2, '0')}:00${suffix}`;
};

export const getStoredPickupSelection = (
  storeId: string,
  userKey?: string,
): StoredPickupSelection | null => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(buildStorageKey(storeId, userKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPickupSelection;
    if (!parsed.dateValue || typeof parsed.hour !== 'number' || !parsed.label) {
      return null;
    }
    return {
      ...parsed,
      label: normalizePickupLabel(parsed.label),
    };
  } catch (error) {
    console.error('Failed to read stored pickup selection', error);
    return null;
  }
};

export const getStoredPickupSelectionForUserKeys = (
  storeId: string,
  userKeys: Array<string | undefined>,
): StoredPickupSelection | null => {
  const seen = new Set<string>();

  for (const rawUserKey of userKeys) {
    const normalizedUserKey = rawUserKey?.trim() || undefined;
    const dedupeKey = normalizedUserKey || '__guest__';
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    const stored = getStoredPickupSelection(storeId, normalizedUserKey);
    if (stored) {
      return stored;
    }
  }

  return null;
};

export const saveStoredPickupSelection = (
  storeId: string,
  selection: StoredPickupSelection,
  userKey?: string,
) => {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(
    buildStorageKey(storeId, userKey),
    JSON.stringify({
      ...selection,
      label: normalizePickupLabel(selection.label),
    }),
  );
};

export const saveStoredPickupSelectionForUserKeys = (
  storeId: string,
  selection: StoredPickupSelection,
  userKeys: Array<string | undefined>,
) => {
  const seen = new Set<string>();

  userKeys.forEach((rawUserKey) => {
    const normalizedUserKey = rawUserKey?.trim() || undefined;
    const dedupeKey = normalizedUserKey || '__guest__';
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);

    saveStoredPickupSelection(storeId, selection, normalizedUserKey);
  });
};

export const getStoredStoreRequestMessage = (
  storeId: string,
  userKey?: string,
) => {
  if (typeof window === 'undefined') return '';

  try {
    return (
      window.localStorage.getItem(buildRequestStorageKey(storeId, userKey)) || ''
    );
  } catch (error) {
    console.error('Failed to read stored request message', error);
    return '';
  }
};

export const getStoredStoreRequestMessageForUserKeys = (
  storeId: string,
  userKeys: Array<string | undefined>,
) => {
  const seen = new Set<string>();

  for (const rawUserKey of userKeys) {
    const normalizedUserKey = rawUserKey?.trim() || undefined;
    const dedupeKey = normalizedUserKey || '__guest__';
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);

    const storedMessage = getStoredStoreRequestMessage(storeId, normalizedUserKey);
    if (storedMessage.trim()) {
      return storedMessage;
    }
  }

  return '';
};

export const saveStoredStoreRequestMessage = (
  storeId: string,
  message: string,
  userKey?: string,
) => {
  if (typeof window === 'undefined') return;

  window.localStorage.setItem(
    buildRequestStorageKey(storeId, userKey),
    message.trim(),
  );
};

export const saveStoredStoreRequestMessageForUserKeys = (
  storeId: string,
  message: string,
  userKeys: Array<string | undefined>,
) => {
  const seen = new Set<string>();

  userKeys.forEach((rawUserKey) => {
    const normalizedUserKey = rawUserKey?.trim() || undefined;
    const dedupeKey = normalizedUserKey || '__guest__';
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);

    saveStoredStoreRequestMessage(storeId, message, normalizedUserKey);
  });
};

export const parseStoreOrderRequestText = (request?: string | null) => {
  const text = request?.trim() || '';
  if (!text) {
    return {
      pickupLabel: '',
      requestMessage: '',
    };
  }

  let pickupLabel = '';
  let requestMessage = '';
  const lines = text.split('\n');

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const trimmedLine = line.trim();

    if (trimmedLine.startsWith(PICKUP_REQUEST_PREFIX)) {
      pickupLabel = normalizePickupLabel(
        trimmedLine.slice(PICKUP_REQUEST_PREFIX.length).trim(),
      );
      continue;
    }

    if (trimmedLine.startsWith(MESSAGE_REQUEST_PREFIX)) {
      const firstMessageLine = trimmedLine
        .slice(MESSAGE_REQUEST_PREFIX.length)
        .trim();
      const remainingMessageLines = lines.slice(index + 1);
      requestMessage = [firstMessageLine, ...remainingMessageLines]
        .join('\n')
        .trim();
      break;
    }

    if (!requestMessage) {
      requestMessage = lines
        .filter(
          (currentLine) =>
            !currentLine.trim().startsWith(PICKUP_REQUEST_PREFIX),
        )
        .join('\n')
        .trim();
    }
  }

  return {
    pickupLabel,
    requestMessage: requestMessage.trim(),
  };
};

export const buildStoreOrderRequestText = (
  pickupLabel?: string | null,
  requestMessage?: string | null,
) => {
  const lines: string[] = [];

  if (pickupLabel?.trim()) {
    lines.push(
      `${PICKUP_REQUEST_PREFIX} ${normalizePickupLabel(pickupLabel)}`,
    );
  }

  if (requestMessage?.trim()) {
    lines.push(`${MESSAGE_REQUEST_PREFIX} ${requestMessage.trim()}`);
  }

  return lines.length > 0 ? lines.join('\n') : undefined;
};

export const buildPickupRequestText = (pickupLabel?: string | null) => {
  return buildStoreOrderRequestText(pickupLabel);
};
